// Custom shape editor: layered primitives with boolean roles compiled into
// the hole outline. Layers are edited numerically (the plan's dimension
// dialogs); the preview is the real compiler output at design scale.

import { useEffect, useMemo, useRef } from 'react'
import { useStore } from '../state/store'
import { compileLayers } from '../core/customShape'
import { defaultShapeLayer, type LayerKind, type ShapeLayer } from '../core/types'
import { ValueField } from './ValueField'
import { pickOutlineFile } from './outlineImport'
import {
  IconBrand,
  IconClose,
  IconImport,
  IconTrash,
} from './icons'

const ADDABLE: { kind: LayerKind; label: string }[] = [
  { kind: 'circle', label: 'Circle' },
  { kind: 'rect', label: 'Rect' },
  { kind: 'slot', label: 'Slot' },
  { kind: 'polygon', label: 'Poly' },
  { kind: 'star', label: 'Star' },
]

export function ShapeEditor() {
  const doc = useStore((s) => s.doc)
  const commit = useStore((s) => s.commit)
  const setOverlay = useStore((s) => s.setOverlay)
  const pushToast = useStore((s) => s.pushToast)
  const layers = doc.customShape.layers

  const addLayer = (kind: LayerKind, outline?: { x: number; y: number }[]) =>
    commit('Add shape layer', (d) => {
      const layer = defaultShapeLayer(kind)
      if (kind !== 'circle' && d.customShape.layers.length > 0) layer.role = 'subtract'
      if (d.customShape.layers.length === 0) layer.role = 'union'
      if (outline) layer.outline = outline
      // Stagger defaults a little so stacked layers are visible immediately.
      if (d.customShape.layers.length > 0) {
        layer.w = 6
        layer.h = 6
      }
      d.customShape.layers.push(layer)
      d.shape.kind = 'custom'
    })

  const importOutline = () =>
    pickOutlineFile(
      (res) => {
        addLayer('outline', res.nodes)
        pushToast(`Outline imported from ${res.source.toUpperCase()} (${res.nodes.length} points)`, 'ok')
      },
      (msg) => pushToast(msg, 'error'),
    )

  const up = (id: string, label: string, fn: (l: ShapeLayer) => void) =>
    commit(label, (d) => {
      const layer = d.customShape.layers.find((x) => x.id === id)
      if (layer) fn(layer)
    })

  const move = (id: string, dir: -1 | 1) =>
    commit('Reorder shape layers', (d) => {
      const list = d.customShape.layers
      const i = list.findIndex((x) => x.id === id)
      const j = i + dir
      if (i < 0 || j < 0 || j >= list.length) return
      const [layer] = list.splice(i, 1)
      list.splice(j, 0, layer)
    })

  return (
    <div className="dialog shape-editor holo-frame" role="dialog" aria-modal="true" aria-label="Custom shape editor">
      <div className="dialog-head">
        <IconBrand size={16} />
        <h2>Custom shape</h2>
        {doc.shape.kind !== 'custom' && layers.length > 0 && (
          <button
            onClick={() =>
              commit('Use custom shape', (d) => {
                d.shape.kind = 'custom'
              })
            }
          >
            Use as hole shape
          </button>
        )}
        <button className="quiet icon-btn" onClick={() => setOverlay(null)} aria-label="Close (Esc)">
          <IconClose size={13} />
        </button>
      </div>
      <div className="dialog-body shape-editor-body">
        <ShapePreview />
        <div className="shape-layers">
          <div className="row add-row">
            {ADDABLE.map((a) => (
              <button key={a.kind} className="icon-btn" onClick={() => addLayer(a.kind)}>
                {a.label}
              </button>
            ))}
            <button className="icon-btn" onClick={importOutline} title="Import an SVG or DXF outline">
              <IconImport size={13} />
              Import
            </button>
          </div>
          {layers.length === 0 && (
            <p className="hint">
              Stack primitives and cut into them: each layer either adds or subtracts. The compiled
              shape replaces the hole primitive wherever Shape kind is Custom.
            </p>
          )}
          {layers.map((l, i) => (
            <div className="controller" key={l.id}>
              <div className="controller-head">
                <span className="controller-kind">
                  {i + 1}. {l.kind}
                </span>
                <select
                  value={l.role}
                  aria-label="Layer role"
                  onChange={(e) => up(l.id, 'Set layer role', (x) => (x.role = e.target.value as 'union' | 'subtract'))}
                >
                  <option value="union">Add</option>
                  <option value="subtract">Cut</option>
                </select>
                <button className="quiet icon-btn" disabled={i === 0} onClick={() => move(l.id, -1)} aria-label="Move layer up">
                  {'↑'}
                </button>
                <button
                  className="quiet icon-btn"
                  disabled={i === layers.length - 1}
                  onClick={() => move(l.id, 1)}
                  aria-label="Move layer down"
                >
                  {'↓'}
                </button>
                <button
                  className="danger icon-btn quiet"
                  aria-label="Delete layer"
                  onClick={() =>
                    commit('Delete shape layer', (d) => {
                      d.customShape.layers = d.customShape.layers.filter((x) => x.id !== l.id)
                    })
                  }
                >
                  <IconTrash size={13} />
                </button>
              </div>
              <div className="row batch-row">
                <ValueField label="X" compact value={l.x} min={-100} max={100} step={0.1} onCommit={(v) => up(l.id, 'Move shape layer', (x) => (x.x = v))} />
                <ValueField label="Y" compact value={l.y} min={-100} max={100} step={0.1} onCommit={(v) => up(l.id, 'Move shape layer', (x) => (x.y = v))} />
              </div>
              <div className="row batch-row">
                <ValueField label="W" compact value={l.w} min={0.1} max={200} step={0.1} onCommit={(v) => up(l.id, 'Resize shape layer', (x) => (x.w = v))} />
                <ValueField label="H" compact value={l.h} min={0.1} max={200} step={0.1} onCommit={(v) => up(l.id, 'Resize shape layer', (x) => (x.h = v))} />
              </div>
              <div className="row batch-row">
                <ValueField label="Rot" compact value={l.rotation} min={-180} max={180} onCommit={(v) => up(l.id, 'Rotate shape layer', (x) => (x.rotation = v))} />
                {l.kind !== 'circle' && l.kind !== 'slot' && (
                  <ValueField
                    label="Fillet"
                    compact
                    value={l.cornerRadius}
                    min={0}
                    max={50}
                    step={0.1}
                    title="Corner radius; reflex corners round too"
                    onCommit={(v) => up(l.id, 'Fillet shape layer', (x) => (x.cornerRadius = v))}
                  />
                )}
              </div>
              {l.kind === 'polygon' && (
                <ValueField label="Sides" compact value={l.sides} min={3} max={12} step={1} onCommit={(v) => up(l.id, 'Set layer sides', (x) => (x.sides = Math.round(v)))} />
              )}
              {l.kind === 'star' && (
                <div className="row batch-row">
                  <ValueField label="Points" compact value={l.starPoints} min={3} max={12} step={1} onCommit={(v) => up(l.id, 'Set layer points', (x) => (x.starPoints = Math.round(v)))} />
                  <ValueField label="Inner" compact value={l.starInner} min={0.1} max={0.95} step={0.05} onCommit={(v) => up(l.id, 'Set layer inner ratio', (x) => (x.starInner = v))} />
                </div>
              )}
              {l.kind === 'outline' && <p className="hint">{l.outline.length} imported points, fit to W x H</p>}
              <div className="row option-row">
                <label className="mini">
                  <input type="checkbox" checked={l.mirrorH} onChange={(e) => up(l.id, 'Mirror shape layer', (x) => (x.mirrorH = e.target.checked))} />
                  Mirror H
                </label>
                <label className="mini">
                  <input type="checkbox" checked={l.mirrorV} onChange={(e) => up(l.id, 'Mirror shape layer', (x) => (x.mirrorV = e.target.checked))} />
                  Mirror V
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function ShapePreview() {
  const doc = useStore((s) => s.doc)
  const theme = useStore((s) => s.theme)
  const ref = useRef<HTMLCanvasElement>(null)
  const polys = useMemo(
    () => compileLayers(doc.customShape, doc.export.tolerance),
    [doc.customShape, doc.export.tolerance],
  )

  useEffect(() => {
    const canvas = ref.current
    if (!canvas) return
    const BOX = 240
    const dpr = window.devicePixelRatio || 1
    canvas.width = BOX * dpr
    canvas.height = BOX * dpr
    canvas.style.width = `${BOX}px`
    canvas.style.height = `${BOX}px`
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const css = getComputedStyle(document.documentElement)
    const tok = (n: string, fb: string) => css.getPropertyValue(n).trim() || fb
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.fillStyle = tok('--cv-bg', '#07090d')
    ctx.fillRect(0, 0, BOX, BOX)
    if (polys.length === 0) {
      ctx.fillStyle = tok('--ink-faint', '#5c6a7b')
      ctx.font = '12px sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText('empty shape', BOX / 2, BOX / 2)
      return
    }
    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity
    for (const poly of polys) {
      for (const [x, y] of poly[0]) {
        minX = Math.min(minX, x)
        minY = Math.min(minY, y)
        maxX = Math.max(maxX, x)
        maxY = Math.max(maxY, y)
      }
    }
    const span = Math.max(maxX - minX, maxY - minY, 1e-9)
    const s = (BOX - 40) / span
    ctx.save()
    ctx.translate(BOX / 2, BOX / 2)
    ctx.scale(s, s)
    ctx.translate(-(minX + maxX) / 2, -(minY + maxY) / 2)
    const path = new Path2D()
    for (const poly of polys) {
      for (const ring of poly) {
        path.moveTo(ring[0][0], ring[0][1])
        for (let i = 1; i < ring.length; i++) path.lineTo(ring[i][0], ring[i][1])
        path.closePath()
      }
    }
    ctx.fillStyle = tok('--accent', '#45e0c8')
    ctx.globalAlpha = 0.85
    ctx.fill(path, 'evenodd')
    ctx.restore()
  }, [polys, theme])

  return (
    <div className="shape-preview">
      <canvas ref={ref} aria-label="Compiled shape preview" />
      <p className="hint">Live compiler output. The size field scales this at placement.</p>
    </div>
  )
}
